import React, { useCallback, useEffect, useState } from 'react';
import { KeyRound, Trash2 } from 'lucide-react';
import {
  deleteAccountLlmCredential,
  listAccountLlmCredentials,
  saveAccountLlmCredential,
} from '../../../api/edgeConnectors';
import Input from '../../../components/ui/Input';
import { getApiErrorMessage } from '../../../utils/errors';

const PROVIDERS = [
  { id: 'openai', label: 'OpenAI' },
  { id: 'anthropic', label: 'Anthropic' },
];

function LlmCredentialsCard({ canManage }) {
  const [configured, setConfigured] = useState({});
  const [values, setValues] = useState({ openai: '', anthropic: '' });
  const [busyProvider, setBusyProvider] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await listAccountLlmCredentials();
      setConfigured(
        Object.fromEntries(
          (result.providers || []).map((item) => [item.provider, item.configured]),
        ),
      );
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível carregar as chaves LLM.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async (event, provider) => {
    event.preventDefault();
    const apiKey = values[provider].trim();
    if (!apiKey) {
      return;
    }

    setBusyProvider(provider);
    setError('');
    setSuccess('');
    try {
      await saveAccountLlmCredential(provider, apiKey);
      setValues((current) => ({ ...current, [provider]: '' }));
      setConfigured((current) => ({ ...current, [provider]: true }));
      setSuccess(`Chave ${provider === 'openai' ? 'OpenAI' : 'Anthropic'} salva.`);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível salvar a chave LLM.'));
    } finally {
      setBusyProvider('');
    }
  };

  const handleRemove = async (provider) => {
    setBusyProvider(provider);
    setError('');
    setSuccess('');
    try {
      await deleteAccountLlmCredential(provider);
      setConfigured((current) => ({ ...current, [provider]: false }));
      setSuccess('Chave removida. A conta voltou a usar a chave padrão da família.');
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível remover a chave LLM.'));
    } finally {
      setBusyProvider('');
    }
  };

  return (
    <div className="surface-card p-4 space-y-4">
      <div className="flex items-start gap-3">
        <KeyRound className="h-5 w-5 text-slate-500 shrink-0 mt-0.5" aria-hidden />
        <div>
          <p className="font-medium text-content-primary">Chaves LLM da conta</p>
          <p className="text-sm text-content-muted mt-1">
            Quando configurada, a chave da conta substitui a chave padrão da família para
            chamadas deste provedor. Modelos, esforço e comportamento Pulse/Nova não mudam.
          </p>
        </div>
      </div>

      {error && <p className="text-sm text-red-700">{error}</p>}
      {success && <p className="text-sm text-emerald-700">{success}</p>}

      {loading ? (
        <p className="text-sm text-content-muted">Carregando chaves...</p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {PROVIDERS.map((provider) => {
            const isConfigured = Boolean(configured[provider.id]);
            const isBusy = busyProvider === provider.id;
            return (
              <form
                key={provider.id}
                onSubmit={(event) => handleSave(event, provider.id)}
                className="rounded-lg border border-slate-200 p-3 space-y-3"
              >
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm font-medium text-content-primary">{provider.label}</p>
                  <span
                    className={`text-xs font-medium ${
                      isConfigured ? 'text-emerald-700' : 'text-amber-700'
                    }`}
                  >
                    {isConfigured ? 'Configurada' : 'Usando padrão'}
                  </span>
                </div>
                {canManage ? (
                  <>
                    <Input
                      id={`llm-key-${provider.id}`}
                      name={`${provider.id}_api_key`}
                      label={isConfigured ? 'Nova chave' : 'API key'}
                      type="password"
                      autoComplete="new-password"
                      value={values[provider.id]}
                      onChange={(event) =>
                        setValues((current) => ({
                          ...current,
                          [provider.id]: event.target.value,
                        }))
                      }
                      placeholder={isConfigured ? 'Preencha para substituir' : 'Cole a chave'}
                      disabled={isBusy}
                      hint="A chave é cifrada e nunca volta pela API."
                    />
                    <div className="flex items-center gap-2">
                      <button
                        type="submit"
                        disabled={isBusy || !values[provider.id].trim()}
                        className="btn-primary text-sm px-3 py-2 disabled:opacity-50"
                      >
                        {isBusy ? 'Salvando...' : isConfigured ? 'Substituir' : 'Salvar'}
                      </button>
                      {isConfigured && (
                        <button
                          type="button"
                          onClick={() => handleRemove(provider.id)}
                          disabled={isBusy}
                          className="btn-secondary text-sm px-3 py-2 inline-flex items-center gap-1.5 disabled:opacity-50"
                        >
                          <Trash2 className="h-4 w-4" aria-hidden />
                          Remover
                        </button>
                      )}
                    </div>
                  </>
                ) : (
                  <p className="text-xs text-content-muted">
                    Somente quem administra o workspace pode alterar esta chave.
                  </p>
                )}
              </form>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default LlmCredentialsCard;
